import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Lock, Eye, EyeOff, ArrowLeft, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PhoneInput from '../components/PhoneInput';
import { invitationsApi } from '../api/invitationsApi';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

const STATUS_PAGES = {
  expired: {
    icon: Clock,
    iconColor: 'text-orange-400',
    bg: 'from-orange-900 via-orange-800 to-orange-900',
    title: 'Davet Süresi Doldu',
    message: 'Bu davet bağlantısının geçerlilik süresi dolmuştur. Lütfen davet eden kişiyle iletişime geçin.',
  },
  cancelled: {
    icon: XCircle,
    iconColor: 'text-red-400',
    bg: 'from-red-900 via-red-800 to-red-900',
    title: 'Davet İptal Edildi',
    message: 'Bu davet iptal edilmiştir. Lütfen davet eden kişiyle iletişime geçiniz.',
  },
  already_accepted: {
    icon: CheckCircle,
    iconColor: 'text-emerald-400',
    bg: 'from-emerald-900 via-green-800 to-emerald-900',
    title: 'Hesap Zaten Oluşturuldu',
    message: 'Bu davet bağlantısı daha önce kullanılmıştır. Hesabınızla giriş yapabilirsiniz.',
    showLogin: true,
  },
  invalid: {
    icon: XCircle,
    iconColor: 'text-red-400',
    bg: 'from-gray-900 via-gray-800 to-gray-900',
    title: 'Geçersiz Bağlantı',
    message: 'Bu davet bağlantısı geçerli değil. Lütfen e-postanızdaki bağlantıyı kontrol edin.',
  },
  success: {
    icon: CheckCircle,
    iconColor: 'text-emerald-400',
    bg: 'from-emerald-900 via-green-800 to-emerald-900',
    title: 'Hoş Geldiniz!',
    message: 'Profiliniz başarıyla oluşturuldu. Sisteme yönlendiriliyorsunuz...',
  },
};

const StatusPage = ({ type, onHome, onLogin }) => {
  const cfg = STATUS_PAGES[type] || STATUS_PAGES.invalid;
  const Icon = cfg.icon;
  return (
    <div className={`min-h-screen bg-gradient-to-br ${cfg.bg} flex items-center justify-center p-4`}>
      <div className="absolute top-6 left-6">
        <button
          onClick={onHome}
          className="flex items-center gap-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 transition-all rounded-full px-4 py-2 text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          Ana Sayfa
        </button>
      </div>
      <div className="text-center max-w-md">
        <div className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-white/10 mb-6">
          <Icon className={`h-14 w-14 ${cfg.iconColor}`} />
        </div>
        <h1 className="text-3xl font-bold text-white mb-4">{cfg.title}</h1>
        <p className="text-white/70 text-lg leading-relaxed mb-8">{cfg.message}</p>
        <div className="flex flex-col gap-3 items-center">
          {cfg.showLogin && (
            <Button
              onClick={onLogin}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-3 font-semibold"
            >
              Giriş Yap
            </Button>
          )}
          <button onClick={onHome} className="text-white/60 hover:text-white text-sm underline">
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    </div>
  );
};

const ProfileForm = ({ inviteInfo, onSubmit, submitting, serverError }) => {
  const [form, setForm] = useState({
    full_name: inviteInfo?.invitee_name || '',
    phone_code: '+90',
    phone_number: '',
    password: '',
    confirm_password: '',
  });
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: '' }));
  };

  const passwordValid = PASSWORD_REGEX.test(form.password);
  const passwordsMatch = form.password === form.confirm_password;

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Ad soyad zorunludur.';
    if (!passwordValid) e.password = 'Şifre kurallarına uygun değil.';
    if (!passwordsMatch) e.confirm_password = 'Şifreler eşleşmiyor.';
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length > 0) { setErrors(e2); return; }
    onSubmit(form);
  };

  const PasswordStrengthHint = () => {
    const rules = [
      { ok: form.password.length >= 8, text: 'En az 8 karakter' },
      { ok: /[A-Z]/.test(form.password), text: 'Büyük harf' },
      { ok: /[a-z]/.test(form.password), text: 'Küçük harf' },
      { ok: /\d/.test(form.password), text: 'Rakam' },
      { ok: /[^A-Za-z\d]/.test(form.password), text: 'Özel karakter' },
    ];
    return (
      <div className="grid grid-cols-2 gap-1 mt-1.5">
        {rules.map((r) => (
          <div key={r.text} className={`text-xs flex items-center gap-1 ${r.ok ? 'text-emerald-600' : 'text-gray-400'}`}>
            <span>{r.ok ? '✓' : '○'}</span> {r.text}
          </div>
        ))}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {serverError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
          {serverError}
        </div>
      )}

      <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm">
        <span className="text-emerald-800 font-medium">Kategori: </span>
        <span className="text-emerald-700">{inviteInfo?.category_name}</span>
        <br />
        <span className="text-emerald-800 font-medium">E-posta: </span>
        <span className="text-emerald-700">{inviteInfo?.email}</span>
      </div>

      <div className="space-y-1.5">
        <Label>Ad Soyad <span className="text-red-500">*</span></Label>
        <Input value={form.full_name} onChange={(e) => set('full_name', e.target.value)} className="rounded-xl" placeholder="Adınız Soyadınız" />
        {errors.full_name && <p className="text-red-500 text-xs">{errors.full_name}</p>}
      </div>

      <div className="space-y-1.5">
        <Label>Cep Telefonu</Label>
        <PhoneInput
          codeValue={form.phone_code}
          numberValue={form.phone_number}
          onCodeChange={(v) => set('phone_code', v)}
          onNumberChange={(v) => set('phone_number', v)}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Şifre <span className="text-red-500">*</span></Label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            type={showPwd ? 'text' : 'password'}
            value={form.password}
            onChange={(e) => set('password', e.target.value)}
            className="pl-10 pr-10 rounded-xl"
            placeholder="••••••••"
          />
          <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-3 text-gray-400">
            {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {form.password && <PasswordStrengthHint />}
        {errors.password && <p className="text-red-500 text-xs">{errors.password}</p>}
      </div>

      <div className="space-y-1.5">
        <Label>Şifre Tekrar <span className="text-red-500">*</span></Label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            type={showConfirm ? 'text' : 'password'}
            value={form.confirm_password}
            onChange={(e) => set('confirm_password', e.target.value)}
            className={`pl-10 pr-10 rounded-xl ${form.confirm_password && !passwordsMatch ? 'border-red-400' : ''}`}
            placeholder="••••••••"
          />
          <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-3 text-gray-400">
            {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {form.confirm_password && !passwordsMatch && (
          <p className="text-red-500 text-xs font-medium">Şifreler eşleşmiyor!</p>
        )}
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full py-6 text-lg font-semibold mt-2"
        data-testid="complete-invite-btn"
      >
        {submitting ? 'Kaydediliyor...' : 'Profili Tamamla ve Giriş Yap'}
      </Button>
    </form>
  );
};

const InviteAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pageState, setPageState] = useState('loading'); // loading | form | expired | cancelled | already_accepted | invalid | success
  const [inviteInfo, setInviteInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const info = await invitationsApi.getInviteInfo(token);
        setInviteInfo(info);
        setPageState('form');
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        // Phase 86AZ.15 — Distinguish "cancelled" vs "already_accepted"; both
        // return 409 from the backend but mean very different things to the
        // invitee. Use the response detail string to disambiguate.
        if (status === 410) setPageState('expired');
        else if (status === 409 && detail === 'cancelled') setPageState('cancelled');
        else if (status === 409 && detail === 'already_accepted') setPageState('already_accepted');
        else if (status === 409) setPageState('cancelled');
        else setPageState('invalid');
      }
    };
    fetchInfo();
  }, [token]);

  const handleComplete = async (formData) => {
    setSubmitting(true);
    setServerError('');
    try {
      const result = await invitationsApi.completeInvite(token, formData);
      // Store auth token
      localStorage.setItem('cop31_token', result.access_token);
      localStorage.setItem('cop31_user', JSON.stringify(result));
      setPageState('success');
      // Phase 86AZ.13 — Send the freshly-onboarded sub-user straight to the
      // customer dashboard (already logged-in), mirroring the standard login
      // redirect. The legacy `/sub-user-profil` page is an empty stop.
      setTimeout(() => {
        const slug = result.tenant_slug || 'default';
        navigate(`/musteri-admin/${slug}/dashboard`, { replace: true });
      }, 1200);
    } catch (err) {
      setServerError(err.response?.data?.detail || 'Profil oluşturulamadı.');
    } finally {
      setSubmitting(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 to-green-800 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="h-10 w-10 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/70">Davet doğrulanıyor...</p>
        </div>
      </div>
    );
  }

  if (pageState !== 'form') {
    return (
      <StatusPage
        type={pageState}
        onHome={() => navigate('/ana-sayfa')}
        onLogin={() => navigate('/giris')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-green-800 to-emerald-900 flex items-center justify-center p-4">
      <div className="absolute top-6 left-6">
        <button
          onClick={() => navigate('/ana-sayfa')}
          className="flex items-center gap-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 transition-all rounded-full px-4 py-2 text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          Ana Sayfa
        </button>
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-emerald-500 mb-4">
            <CheckCircle className="h-12 w-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Profilinizi Tamamlayın</h1>
          <p className="text-emerald-200 text-sm">COP 31 Antalya Platformu</p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <ProfileForm
            inviteInfo={inviteInfo}
            onSubmit={handleComplete}
            submitting={submitting}
            serverError={serverError}
          />
        </div>

        <p className="text-center text-emerald-200 mt-6 text-sm">
          © 2026 COP 31 Baku. Güvenli kayıt.
        </p>
      </div>
    </div>
  );
};

export default InviteAccept;
